import { formatMultipleSortingForEndpoint } from '@app/components/data-table/lib/parsers';
import Page from '@app/components/layout/page/page';
import { useDataTableQueryState } from '@app/hooks/use-data-table-query-state';
import useSimpleDialog from '@app/hooks/use-simple-dialog';
import { useGetBusinessTravelActivityList } from '@shared/api/hooks/ccf/activities/business-travel';
import { CompanyReportsActivitiesBusinessTravelListSortItem } from '@shared/api/types';
import Text from '@shared/components/content/text';
import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsString,
  useQueryState,
} from 'nuqs';
import { useCCF } from '../../ccf-context';
import BusinessTravelEditForm from '../../components/scope-3/business-travel/business-travel-edit-form';
import BusinessTravelTable from '../../components/scope-3/business-travel/business-travel-table';
import { reportSlugs } from '../../slugs';
import { useCategory } from '../../utils';
import CCFCategoryIntroPage from '../ccf-category-intro-page';

export default function BusinessTravelPage() {
  const { uuid: reportUuid } = useCCF();
  const category = useCategory(reportSlugs.businessTravel);
  const {
    showSimpleDialog: showBusinessTravelDialog,
    SimpleDialogComponent: BusinessTravelDialog,
    closeSimpleDialog: closeBusinessTravelDialog,
  } = useSimpleDialog();

  const { page, perPage, period, site, sort, fuel } = useDataTableQueryState({
    filters: ['period', 'site', 'fuel'],
  });

  const [methodology] = useQueryState(
    'methodology',
    parseAsArrayOf(parseAsString).withDefault([])
  );
  const [includeSecr] = useQueryState(
    'includeSecr',
    parseAsBoolean.withDefault(false)
  );

  const {
    data,
    isPreviousData,
    isFetchedAfterMount,
    isLoading,
    isFetching: _isFetching,
    status,
  } = useGetBusinessTravelActivityList(reportUuid!, {
    page,
    page_size: perPage,
    time_periods: period || undefined,
    sites: site || undefined,
    fuels: fuel || undefined,
    input_methodologies: methodology?.length ? methodology : undefined,
    include_secr: includeSecr || undefined,
    sort: formatMultipleSortingForEndpoint(
      sort! || []
    ) as CompanyReportsActivitiesBusinessTravelListSortItem[],
  });

  const description = (
    <>
      <Text>
        Scope 3, Category 6 — Business Travel refers to emissions created when
        your employees travel for work using transport not owned or operated by
        your company.
      </Text>
      <Text className="mt-3">
        We support three methodologies for inputting business travel data:
      </Text>
      <ul className="mt-2 list-disc space-y-1 pl-5">
        <li>
          <Text>
            <span className="font-semibold">Fuel usage</span> - based on the
            amount of each fuel you purchased
          </Text>
        </li>
        <li>
          <Text>
            <span className="font-semibold">Distance</span> - based on how far
            passengers or vehicles travelled
          </Text>
        </li>
        <li>
          <Text>
            <span className="font-semibold">Total emissions</span> - based on
            pre-calculated emission values from outside our platform
          </Text>
        </li>
      </ul>
    </>
  );

  return (
    <Page name="Scope 3 - Business travel" page="subpage">
      {category?.hasActivityData ? (
        <BusinessTravelTable
          data={data!}
          status={status}
          isLoading={isLoading}
          isPreviousData={isPreviousData}
          isFetchedAfterMount={isFetchedAfterMount}
        />
      ) : (
        <CCFCategoryIntroPage
          categorySlug={reportSlugs.businessTravel}
          description={description}
          primaryActionProps={{
            onClick: showBusinessTravelDialog,
          }}
        />
      )}
      {BusinessTravelDialog({
        header: 'Add business travel data',
        contentClassName: 'max-w-3xl',
        content: (
          <BusinessTravelEditForm closeDialog={closeBusinessTravelDialog} />
        ),
      })}
    </Page>
  );
}
